import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type FC, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import type { AgentClientContextDto } from '../core/_models';
import { deriveScreenContext } from './deriveScreenContext';

type SelectedEntities = Record<string, unknown>;

type AgentContextValue = {
  setEntities: (entities: SelectedEntities) => void;
  clearEntities: () => void;
  getClientContext: () => AgentClientContextDto;
};

const AgentContext = createContext<AgentContextValue | undefined>(undefined);

const AgentContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const entitiesRef = useRef<SelectedEntities>({});
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  const setEntities = useCallback((entities: SelectedEntities) => {
    entitiesRef.current = entities;
  }, []);

  const clearEntities = useCallback(() => {
    entitiesRef.current = {};
  }, []);

  const getClientContext = useCallback((): AgentClientContextDto => {
    const { module, screen } = deriveScreenContext(pathnameRef.current);
    return { module, screen, selected_entities: entitiesRef.current };
  }, []);

  const value = useMemo(
    () => ({ setEntities, clearEntities, getClientContext }),
    [setEntities, clearEntities, getClientContext],
  );

  return <AgentContext.Provider value={value}>{children}</AgentContext.Provider>;
};

const useAgentContextValue = (): AgentContextValue => {
  const value = useContext(AgentContext);
  if (!value) throw new Error('useAgentContext must be used within AgentContextProvider');
  return value;
};

const useAgentSelectedEntities = (entities: SelectedEntities) => {
  const { setEntities, clearEntities } = useAgentContextValue();
  // Refresh every render so event handlers read the latest selection.
  useEffect(() => {
    setEntities(entities);
  });
  useEffect(() => clearEntities, [clearEntities]);
};

const useAgentClientContext = () => useAgentContextValue().getClientContext;

export { AgentContextProvider, useAgentSelectedEntities, useAgentClientContext };
